import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { AppDispatch, RootStateType } from '@Store';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SBDropdown from '@Components/Dropdown';
import { useFormik } from 'formik';
import SbButton from '@Components/Button';
import {
  CSBTypeOptions,
  IsHavingBatteryOptions,
  TaxPaymentOptions,
  TermsOfInvoiceOptions,
  onForward,
  batteryPackingOptions,
  batteryTypeOptions,
} from './constants';
import * as Yup from 'yup';
import { getCarrierSettings } from '@Reducers/User/actions';
import Loader from '@Components/Loader';
import { DropdownOptionType } from '@Components/Dropdown/Type';
import {
  getWarehouses,
  addWarehouse,
} from '../../../ViewSingleShipmentAdmin/components/ShipmentProcessingV2/components/Thinklogistics/constants';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import SbTextField from '@Components/Textfield';
import { getVendorSettingList } from '@Reducers/Shipment/actions';
import { userLogin } from '@Services/hasAdminAccess';

// ------- VALIDATION SCHEMA -------
const iThinkLogisticsInformationSchema = Yup.object().shape({
  type: Yup.string().required('Type is required'),
  accountNo: Yup.mixed()
    .test('is-valid', 'Account is required', (value: any) => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object') return true;
      return !!value;
    })
    .required('Account is required'),
  csb_type: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('CSB Type is required'),
  terms_of_invoice: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Terms of invoice is required'),
  tax_payment_option: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Tax payment is required'),
  is_having_battery: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Is having battery is required'),
  pickup_address_id: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Warehouse address is required'),
  // Conditional validation for battery fields
  battery_packing: Yup.string().when('is_having_battery', {
    is: 'yes',
    then: schema => schema.required('Battery packing is required'),
    otherwise: schema => schema.notRequired(),
  }),
  battery_type: Yup.string().when('is_having_battery', {
    is: 'yes',
    then: schema => schema.required('Battery type is required'),
    otherwise: schema => schema.notRequired(),
  }),
  HSNCode: Yup.string().required('HSNCode is required'),
});

interface propsTypes {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showFedExDetails: boolean;
  btnText: string;
}

const UserThinkLogistics = (props: propsTypes) => {
  const { onClickForward, onClose, initialStates, isEditable, showFedExDetails, btnText } = props;
  const dispatch = useDispatch<AppDispatch>();
  const isUserLogin = userLogin();
  const [loading, setLoading] = useState<boolean>(false);
  const [isWarehouseLoading, setIsWarehouseLoading] = useState<boolean>(false);
  const invoiceNo = useSelector((state: RootStateType) => state.shipment.shipmentById?._packageId.invoiceNumber);
  const HSNCodeValue =
    useSelector((state: RootStateType) => state.shipment.shipmentById?._packageId?.box?.[0]?.products) || [];
  const { shipmentById, userIThinkLogisticsInformationDetails: iThinkLogisticsSettings } = useSelector(
    (state: RootStateType) => state.shipment,
  );
  const [pickupAddressOptions, setPickupAddressOptions] = useState<DropdownOptionType[]>([]);
  const { thinkLogisticsDetails } = useSelector((state: RootStateType) => state.adminShipment);
  const { token } = useSelector((state: RootStateType) => state.loginUser);

  // Hardcoded "Add Warehouse" option
  const addWarehouseOption: DropdownOptionType = {
    value: 'add_warehouse',
    display: '+ Add Warehouse',
  };

  // Function to get warehouse options based on iThinkLogisticsSettings
  const getWarehouseOptions = () => {
    // If iThinkLogisticsSettings has pickup_address_id with length, include add warehouse option
    if (iThinkLogisticsSettings && iThinkLogisticsSettings?.pickup_address_id?.length > 0) {
      return [addWarehouseOption, ...iThinkLogisticsSettings.pickup_address_id];
    }

    // Otherwise use the combined options with fetched warehouses
    return [addWarehouseOption, ...pickupAddressOptions];
  };

  const fetchWarehouses = async (accountNumber: string) => {
    try {
      const warehouses = await getWarehouses(accountNumber);
      setPickupAddressOptions(warehouses);
    } catch (error) {
      console.error('Failed to fetch warehouses', error);
    }
  };

  const handleWarehouseChange = async (value: string[]) => {
    const selectedValue = value[0] || '';

    if (selectedValue === 'add_warehouse') {
      // Handle "Add Warehouse" option click
      if (!values.accountNo || (Array.isArray(values.accountNo) && values.accountNo.length === 0)) {
        dispatch(
          SuccessErrorModalOpen({
            type: 'error',
            title: 'Oops!',
            subTitle: 'Please select an iThink Logistics Account first',
          }),
        );
        setFieldValue('pickup_address_id', '');
        return;
      }

      setIsWarehouseLoading(true);
      try {
        // Extract account number from the selected account
        let accountNumber = '';
        if (Array.isArray(values.accountNo)) {
          // When accountNo is an array of objects with value property
          const accountValue = values.accountNo[0]?.value || '';
          accountNumber = accountValue.split('-')[0];
        } else {
          // When accountNo is a string
          accountNumber = values.accountNo.split('-')[0];
        }

        const shipmentNumber = shipmentById?.shipmentNumber || '';
        await addWarehouse({ accountNo: accountNumber, shipmentNumber });

        // Refresh the warehouses list after successful addition
        await fetchWarehouses(accountNumber);

        dispatch(
          SuccessErrorModalOpen({
            type: 'success',
            title: 'Success!',
            subTitle: 'Warehouse added successfully!',
          }),
        );
        if (isUserLogin && shipmentById?.buyingPricing?.code && token) {
          dispatch(
            getVendorSettingList({
              csp: shipmentById?.buyingPricing?.code,
              accountNumber: token?.accountNumber,
            }),
          );
        }
      } catch (error) {
        dispatch(
          SuccessErrorModalOpen({
            type: 'error',
            title: 'Error',
            subTitle: error?.response?.data?.msg || 'Failed to add warehouse. Please try again.',
          }),
        );
        console.error('Error adding warehouse:', error);
      } finally {
        setIsWarehouseLoading(false);
        setFieldValue('pickup_address_id', '');
      }
    } else {
      // Normal warehouse selection
      setFieldValue('pickup_address_id', selectedValue);
    }
  };

  // ------------ FORMIK ------------
  const { values, setFieldValue, errors, handleSubmit } = useFormik<any>({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      battery_packing: '',
      battery_type: '',
      HSNCode: HSNCodeValue?.[0]?.HSNCode || '',
      ...initialStates,
      invoiceNumber: invoiceNo,
      accountNo: iThinkLogisticsSettings?.accountNo
        ? iThinkLogisticsSettings?.accountNo.filter((acc: any) => acc.value.startsWith(initialStates.accountNo))
        : [],
    },
    validationSchema: iThinkLogisticsInformationSchema,
    onSubmit(values) {
      onForward({
        values,
        shipmentNumber: shipmentById?.shipmentNumber || '',
        dispatch,
        nextFunc: onClickForward,
        setLoading,
      });
    },
  });

  const accountNumber = values?.accountNo?.[0]?.value.split('-')[0];
  useEffect(() => {
    if (accountNumber && shipmentById?.buyingPricing?.code === 'ithinklogistics') {
      fetchWarehouses(accountNumber);
    }
  }, [shipmentById, accountNumber]);

  useEffect(() => {
    if (shipmentById?._accountId.accountNumber) {
      dispatch(
        getCarrierSettings({
          csp: 'ithinklogistics',
          accountNumber: shipmentById?._accountId.accountNumber,
        }),
      );
    }
  }, [shipmentById?._accountId.accountNumber]);

  // Check if battery fields should be shown
  const showBatteryFields = values.is_having_battery === 'yes';

  // Handle battery selection change
  const handleBatterySelectionChange = (value: string[]) => {
    const selected = value[0] || '';
    setFieldValue('is_having_battery', selected);

    // Clear battery fields when battery option changes to anything other than "yes"
    if (selected !== 'yes') {
      setFieldValue('battery_packing', 'NA');
      setFieldValue('battery_type', 'NA');
    }
  };

  return (
    <>
      <Loader overly loading={loading}></Loader>
      {showFedExDetails && (
        <Paper
          variant="outlined"
          sx={{
            my: '2rem',
            p: '2rem',
          }}
        >
          <Grid container item xs={12}>
            <Grid container item mb={3}>
              <Grid item xs={12} md={6}>
                <Typography className="font-medium l-text pb-3">iThink Logistics Information</Typography>{' '}
              </Grid>
            </Grid>
            <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="accountNo"
                    label="iThink Logistics Account"
                    required
                    placeholder="Select"
                    name="accountNo"
                    value={
                      Array.isArray(values?.accountNo)
                        ? values.accountNo.map((item: { value: any }) => item.value)
                        : values.accountNo
                        ? [values.accountNo]
                        : []
                    }
                    options={iThinkLogisticsSettings?.accountNo ?? []}
                    onChange={(_, selectedObj) => {
                      setFieldValue('accountNo', selectedObj);
                    }}
                    error={errors.accountNo as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="csb_type"
                    label="CSB Type"
                    required
                    placeholder="Select"
                    name="csb_type"
                    value={values.csb_type ? [values.csb_type] : []}
                    options={
                      iThinkLogisticsSettings?.csb_type?.length ? iThinkLogisticsSettings.csb_type : CSBTypeOptions
                    }
                    onChange={val => setFieldValue('csb_type', val[0] || '')}
                    error={errors.csb_type as string}
                  />
                </Grid>
              </Grid>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="terms_of_invoice"
                    label="Terms of Invoice"
                    required
                    placeholder="Select"
                    name="terms_of_invoice"
                    value={values.terms_of_invoice ? [values.terms_of_invoice] : []}
                    options={
                      iThinkLogisticsSettings?.terms_of_invoice?.length
                        ? iThinkLogisticsSettings.terms_of_invoice
                        : TermsOfInvoiceOptions
                    }
                    onChange={val => {
                      const selected = val[0] || '';
                      setFieldValue('terms_of_invoice', selected);
                    }}
                    error={errors.terms_of_invoice as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="tax_payment_option"
                    label="Tax Payment Option"
                    required
                    placeholder="Select"
                    name="tax_payment_option"
                    value={values.tax_payment_option ? [values.tax_payment_option] : []}
                    options={
                      iThinkLogisticsSettings?.tax_payment_option?.length
                        ? iThinkLogisticsSettings.tax_payment_option
                        : TaxPaymentOptions
                    }
                    onChange={val => setFieldValue('tax_payment_option', val[0] || '')}
                    error={errors.tax_payment_option as string}
                  />
                </Grid>
              </Grid>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="is_having_battery"
                    label="Is Having Battery"
                    required
                    placeholder="Select"
                    name="is_having_battery"
                    value={values.is_having_battery ? [values.is_having_battery] : []}
                    options={
                      iThinkLogisticsSettings?.is_having_battery?.length
                        ? iThinkLogisticsSettings.is_having_battery
                        : IsHavingBatteryOptions
                    }
                    onChange={val => handleBatterySelectionChange(val)}
                    error={errors.is_having_battery as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable || isWarehouseLoading}
                    id="pickup_address_id"
                    label="Warehouse Address"
                    required
                    placeholder="Select"
                    name="pickup_address_id"
                    value={values.pickup_address_id ? [values.pickup_address_id] : []}
                    options={getWarehouseOptions()}
                    onChange={handleWarehouseChange}
                    error={errors.pickup_address_id as string}
                  />
                  {isWarehouseLoading && (
                    <Typography variant="caption" sx={{ color: 'primary.main', mt: 1, display: 'block' }}>
                      Adding warehouse, please wait...
                    </Typography>
                  )}
                </Grid>
              </Grid>

              {/* Conditionally render battery fields */}
              {showBatteryFields && (
                <Grid container item columnSpacing={10} rowSpacing={2}>
                  <Grid item xs={12} md={6}>
                    <SBDropdown
                      disabled={!isEditable}
                      id="battery_packing"
                      label="Battery Packing"
                      required
                      placeholder="Select"
                      name="battery_packing"
                      value={values.battery_packing ? [values.battery_packing] : []}
                      options={
                        iThinkLogisticsSettings?.battery_packing?.length
                          ? iThinkLogisticsSettings.battery_packing
                          : batteryPackingOptions
                      }
                      onChange={val => setFieldValue('battery_packing', val[0] || '')}
                      error={errors.battery_packing as string}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SBDropdown
                      disabled={!isEditable}
                      id="battery_type"
                      label="Battery Type"
                      required
                      placeholder="Select"
                      name="battery_type"
                      value={values.battery_type ? [values.battery_type] : []}
                      options={
                        iThinkLogisticsSettings?.battery_type?.length
                          ? iThinkLogisticsSettings.battery_type
                          : batteryTypeOptions
                      }
                      onChange={val => setFieldValue('battery_type', val[0] || '')}
                      error={errors.battery_type as string}
                    />
                  </Grid>
                </Grid>
              )}
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SbTextField
                    placeholder="Enter HSN Code"
                    label="HSN Code"
                    required
                    onChange={e => setFieldValue('HSNCode', e.target.value)}
                    name="HSNCode"
                    value={values.HSNCode}
                    error={errors.HSNCode as string}
                    disabled={!isEditable}
                  />
                </Grid>
              </Grid>

              <Divider className="mt-3 mb-3" />
              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid container item sx={{ gap: '1rem' }}>
                  <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                    <SbButton
                      disabled={!isEditable || isWarehouseLoading}
                      variant="contained"
                      sx={{ borderRadius: '4px !important' }}
                      className="w-full"
                      onClick={_event => {
                        handleSubmit();
                      }}
                    >
                      {btnText}
                    </SbButton>
                  </Grid>
                  {onClose && (
                    <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                      <SbButton
                        sx={{ borderRadius: '4px !important' }}
                        className="w-full"
                        variant="outlined"
                        onClick={onClose}
                      >
                        Cancel
                      </SbButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}
      {thinkLogisticsDetails?.response && (
        <Grid container item>
          <Grid item xs={'auto'} border={'1px solid'} className="border-grey font-medium md-text p-2" borderRadius={1}>
            This shipment has been forwarded.
            <Box
              component={'span'}
              className="font-medium md-text ml-1"
              sx={{
                color: '#485fc7',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  dispatch(downloadCarrierLabel(shipmentById?.shipmentNumber));
                }
              }}
            >
              Download the carrier AWB
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default UserThinkLogistics;
